// Board switcher dropdown (desktop apps only — the web has a URL bar). Anchored
// under the toolbar button (and the landing "Open existing board" button): it
// lists recently-opened boards, a "New board" action, and a field to open a
// board by pasted id/link. Lightweight menu, not a centered modal.

import { apiUrl } from "../config/backend";
import { parseBoardRef } from "../shared/ids";
import { listRecentBoards } from "../storage/recent-boards";
import { navigateToBoard } from "../main";

const MENU_CLASS = "boards-menu";

let outsideHandler: ((e: MouseEvent) => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;

/** Open the board-switcher dropdown anchored beneath `anchor`. */
export function openBoardsMenu(anchor: HTMLElement): void {
  if (closeBoardsMenu()) return; // clicking the trigger again just closes it.

  const menu = document.createElement("div");
  menu.className = MENU_CLASS;
  menu.setAttribute("role", "menu");

  const recents = listRecentBoards();
  if (recents.length > 0) {
    const label = document.createElement("div");
    label.className = "boards-menu__label";
    label.textContent = "Recent boards";
    const list = document.createElement("ul");
    list.className = "boards-menu__list";
    for (const b of recents) {
      const li = document.createElement("li");
      const item = document.createElement("button");
      item.type = "button";
      item.className = "boards-menu__item";
      item.setAttribute("role", "menuitem");
      const title = document.createElement("span");
      title.className = "boards-menu__item-title";
      title.textContent = b.title;
      const id = document.createElement("span");
      id.className = "boards-menu__item-id";
      id.textContent = b.id;
      item.append(title, id);
      item.addEventListener("click", () => {
        closeBoardsMenu();
        navigateToBoard(b.id);
      });
      li.append(item);
      list.append(li);
    }
    menu.append(label, list);
  }

  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.className = "boards-menu__new";
  newBtn.setAttribute("role", "menuitem");
  newBtn.textContent = "New board";
  newBtn.addEventListener("click", () => void createBoard(newBtn));
  menu.append(newBtn);

  // Open-by-id/link — preserves opening a board someone shared with you, which
  // the app otherwise has no URL bar for.
  const form = document.createElement("form");
  form.className = "boards-menu__open";
  const input = document.createElement("input");
  input.type = "text";
  input.className = "boards-menu__open-input";
  input.placeholder = "Open by ID or link";
  input.autocapitalize = "off";
  input.autocomplete = "off";
  input.spellcheck = false;
  form.append(input);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const id = parseBoardRef(input.value);
    if (!id) {
      input.classList.add("boards-menu__open-input--invalid");
      input.focus();
      return;
    }
    closeBoardsMenu();
    navigateToBoard(id);
  });
  input.addEventListener("input", () =>
    input.classList.remove("boards-menu__open-input--invalid"),
  );
  menu.append(form);

  document.body.append(menu);
  position(menu, anchor);

  // Close on outside click / Escape; reposition on resize is unnecessary for a
  // transient menu (it closes on the next interaction anyway).
  outsideHandler = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node) && e.target !== anchor) closeBoardsMenu();
  };
  keyHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeBoardsMenu();
      anchor.focus();
    }
  };
  // Defer the outside-click listener so the opening click doesn't immediately close it.
  setTimeout(() => {
    if (outsideHandler) document.addEventListener("click", outsideHandler);
  }, 0);
  document.addEventListener("keydown", keyHandler);

  (recents.length > 0
    ? menu.querySelector<HTMLElement>(".boards-menu__item")
    : newBtn
  )?.focus();
}

/** Close the menu if open. Returns true if a menu was actually closed. */
export function closeBoardsMenu(): boolean {
  const existing = document.querySelector(`.${MENU_CLASS}`);
  if (outsideHandler) {
    document.removeEventListener("click", outsideHandler);
    outsideHandler = null;
  }
  if (keyHandler) {
    document.removeEventListener("keydown", keyHandler);
    keyHandler = null;
  }
  if (!existing) return false;
  existing.remove();
  return true;
}

async function createBoard(btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true;
  const prev = btn.textContent;
  btn.textContent = "Creating…";
  try {
    const r = await fetch(apiUrl("/api/new"), { method: "POST" });
    if (!r.ok) throw new Error(`/api/new returned ${r.status}`);
    const body = (await r.json()) as { id: string };
    closeBoardsMenu();
    navigateToBoard(body.id);
  } catch {
    // Restore the button and surface a terse inline error.
    btn.disabled = false;
    btn.textContent = prev;
    btn.classList.add("boards-menu__new--error");
  }
}

/** Anchor the menu below `anchor`, right-aligned to it and clamped to the viewport. */
function position(menu: HTMLElement, anchor: HTMLElement): void {
  const r = anchor.getBoundingClientRect();
  menu.style.position = "fixed";
  menu.style.top = `${Math.round(r.bottom + 6)}px`;
  const right = Math.max(8, Math.round(window.innerWidth - r.right));
  menu.style.right = `${right}px`;
}
